import { useState, useCallback, FormEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo } from '@/mocks/comics';

const FORM_SUBMIT_URL = 'http://xinzhewash.com/';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 奇妙漫画网';
  }, []);

  const handleSubmit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    try {
      const res = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (serverMsg && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
        setFormStatus('error');
        setFormError(serverMsg);
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  }, []);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '奇妙漫画网是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，奇妙漫画网所有韩国漫画均为免费在线阅读，无需付费即可享受完整阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '奇妙漫画网有多少部韩国漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `奇妙漫画网累计收录韩国漫画超过${siteInfo.comicCount}部，涵盖恋爱、奇幻、动作、剧情等12个分类。`,
        },
      },
      {
        '@type': 'Question',
        name: '漫画更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们与多家韩国漫画工作室合作，确保热门连载漫画每日更新，让您第一时间追更最新章节。',
        },
      },
      {
        '@type': 'Question',
        name: '需要注册账号才能阅读吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要，奇妙漫画网所有内容均可免费在线阅读，无需注册或登录。',
        },
      },
    ],
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: typeof window !== 'undefined' ? 'http://xinzhewash.com' : '',
    description: siteInfo.description,
    email: siteInfo.email,
    foundingDate: siteInfo.foundingYear,
    contactPoint: {
      '@type': 'ContactPoint',
      email: siteInfo.email,
      contactType: 'customer service',
    },
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: siteInfo.about,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
        { '@type': 'ListItem', position: 2, name: '关于我们' },
      ],
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
      />

      <Navbar />

      <main>
        <section className="relative w-full min-h-[420px] md:min-h-[500px] flex items-end pt-16">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20artistic%20abstract%20background%20with%20soft%20amber%20and%20cream%20tones%2C%20elegant%20flowing%20shapes%2C%20professional%20teamwork%20atmosphere%2C%20modern%20office%20aesthetic%2C%20creative%20workspace%20vibe%2C%20warm%20gradient%2C%20clean%20minimal%20design%2C%20professional%20banner%20style%2C%20soft%20lighting%20and%20harmonious%20composition&width=1600&height=700&seq=about-hero-01&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/45 to-black/25"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 pb-10">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />
              <h1 className="mt-6 text-3xl md:text-5xl font-black text-background-50 font-heading">
                关于我们
              </h1>
              <p className="text-sm text-background-200/70 mt-2">
                了解奇妙漫画网的故事与愿景
              </p>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 lg:px-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10 lg:gap-20">
                <div className="w-full lg:w-[40%]">
                  <span className="text-xs font-medium text-primary-500 tracking-wider">
                    （品牌故事）
                  </span>
                </div>
                <div className="w-full lg:w-[60%]">
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 font-heading mb-6 leading-snug">
                    让每一位漫画爱好者
                    <br />
                    <span className="text-foreground-300">享受优质的韩漫内容</span>
                  </h2>
                  <article>
                    <p className="text-base text-foreground-600 leading-relaxed mb-5">
                      {siteInfo.about}
                    </p>
                    <p className="text-base text-foreground-600 leading-relaxed mb-5">
                      我们的团队由一群热爱韩国漫画的年轻人组成，致力于打造国内最好的韩漫在线阅读平台。我们深知漫画爱好者的需求——快速更新、高清画质、流畅阅读，这些都是我们不懈追求的目标。
                    </p>
                    <p className="text-base text-foreground-600 leading-relaxed">
                      通过不断优化推荐算法和用户体验，奇妙漫画网已成为数百万漫画爱好者的首选平台。无论你是资深韩漫迷还是刚刚入门的新读者，在这里都能找到属于你的精彩故事。
                    </p>
                  </article>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
                    <div className="text-center p-4">
                      <div className="text-2xl md:text-3xl font-black text-primary-500 font-heading">
                        {siteInfo.comicCount}
                      </div>
                      <p className="text-xs text-foreground-500 mt-1">漫画资源</p>
                    </div>
                    <div className="text-center p-4">
                      <div className="text-2xl md:text-3xl font-black text-primary-500 font-heading">
                        {siteInfo.userCount}
                      </div>
                      <p className="text-xs text-foreground-500 mt-1">月活用户</p>
                    </div>
                    <div className="text-center p-4">
                      <div className="text-2xl md:text-3xl font-black text-primary-500 font-heading">
                        {siteInfo.categoryCount}
                      </div>
                      <p className="text-xs text-foreground-500 mt-1">分类覆盖</p>
                    </div>
                    <div className="text-center p-4">
                      <div className="text-2xl md:text-3xl font-black text-primary-500 font-heading">
                        4.9
                      </div>
                      <p className="text-xs text-foreground-500 mt-1">用户评分</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-10">
                    <div className="rounded-xl overflow-hidden aspect-[16/10] bg-background-200">
                      <img
                        src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20workspace%20with%20warm%20lighting%2C%20team%20collaboration%2C%20minimal%20interior%20design%2C%20wooden%20desks%2C%20plants%2C%20comfortable%20atmosphere%2C%20professional%20photography%2C%20clean%20composition%2C%20bright%20natural%20light%20through%20windows&width=600&height=375&seq=about-office-02&orientation=landscape"
                        alt="奇妙漫画网办公环境"
                        title="奇妙漫画网团队办公环境"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="rounded-xl overflow-hidden aspect-[16/10] bg-background-200">
                      <img
                        src="https://readdy.ai/api/search-image?query=Diverse%20creative%20team%20meeting%20in%20modern%20office%2C%20collaborative%20atmosphere%2C%20warm%20lighting%2C%20casual%20professional%20style%2C%20brainstorming%20session%2C%20young%20Asian%20professionals%2C%20clean%20photography%2C%20genuine%20smiles%20and%20engagement&width=600&height=375&seq=about-team-03&orientation=landscape"
                        alt="奇妙漫画网团队合影"
                        title="奇妙漫画网团队"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 lg:px-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10 lg:gap-20">
                <div className="w-full lg:w-[40%]">
                  <span className="text-xs font-medium text-primary-500 tracking-wider">
                    （联系方式）
                  </span>
                </div>
                <div className="w-full lg:w-[60%]">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-6">
                    联系我们
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className="ri-mail-line text-primary-500"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-950">电子邮箱</h4>
                        <a
                          href={`mailto:${siteInfo.email}`}
                          className="text-sm text-foreground-500 hover:text-primary-500 transition-colors"
                        >
                          {siteInfo.email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className="ri-time-line text-primary-500"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-950">工作时间</h4>
                        <p className="text-sm text-foreground-500">
                          周一至周五 9:00 - 18:00
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className="ri-map-pin-line text-primary-500"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-950">公司地址</h4>
                        <p className="text-sm text-foreground-500">
                          上海市浦东新区张江高科技园区
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-10 p-6 md:p-8 rounded-xl bg-background-100">
                    <h3 className="text-lg font-bold text-foreground-950 font-heading mb-4">
                      合作咨询
                    </h3>
                    <form
                      className="space-y-4"
                      onSubmit={handleSubmit}
                     
                    >
                      <div>
                        <label htmlFor="contact_name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名
                        </label>
                        <input
                          type="text"
                          id="contact_name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱
                        </label>
                        <input
                          type="email"
                          id="contact_email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言
                        </label>
                        <textarea
                          id="contact_message"
                          name="message"
                          rows={4}
                          maxLength={500}
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言（限500字）"
                        ></textarea>
                      </div>
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />
                      <button
                        type="submit"
                        className="whitespace-nowrap w-full px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
                      >
                        发送留言
                      </button>

                      {formStatus === 'success' && (
                        <p className="text-sm text-secondary-600 font-medium text-center">
                          留言发送成功！我们会尽快回复您。
                        </p>
                      )}
                      {formStatus === 'error' && (
                        <p className="text-sm text-primary-500 font-medium text-center">
                          {formError || '提交失败，请稍后重试'}
                        </p>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 lg:px-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10 lg:gap-20">
                <div className="w-full lg:w-[40%]">
                  <span className="text-xs font-medium text-primary-500 tracking-wider">
                    （常见问题）
                  </span>
                </div>
                <div className="w-full lg:w-[60%]">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-8">
                    常见问题
                  </h2>
                  <div className="space-y-4">
                    {faqJsonLd.mainEntity.map((faq, idx) => (
                      <details key={idx} className="group rounded-lg bg-background-50 p-5 cursor-pointer">
                        <summary className="text-sm font-semibold text-foreground-950 list-none flex items-center justify-between">
                          {faq.name}
                          <i className="ri-add-line group-open:hidden text-foreground-400"></i>
                          <i className="ri-subtract-line hidden group-open:block text-foreground-400"></i>
                        </summary>
                        <p className="text-sm text-foreground-600 mt-3 leading-relaxed">
                          {faq.acceptedAnswer.text}
                        </p>
                      </details>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}